import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  GripVertical, 
  Pencil, 
  Check, 
  X, 
  Plus, 
  Trash2, 
  RotateCcw,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { dayModes, getDefaultTasks } from '../utils/dayModes';
import { getCustomTasks, saveCustomTasks, resetCustomTasks } from '../utils/localStorage';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const TaskEditor = () => {
  const [selectedMode, setSelectedMode] = useState('survival');
  const [tasks, setTasks] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Load tasks when mode changes
  useEffect(() => {
    loadTasks();
  }, [selectedMode]);

  const loadTasks = () => {
    const customTasks = getCustomTasks(selectedMode);
    if (customTasks) {
      setTasks(customTasks);
    } else {
      setTasks([...getDefaultTasks(selectedMode)]);
    }
    setHasChanges(false);
  };

  const handleModeChange = (mode) => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Switch mode anyway?')) {
        return;
      }
    }
    setSelectedMode(mode);
    setEditingId(null);
  };

  const startEditing = (task) => {
    setEditingId(task.id);
    setEditText(task.text);
  };

  const saveEdit = () => {
    if (!editText.trim()) {
      toast.error('Task name cannot be empty');
      return;
    }
    
    setTasks(tasks.map(t => 
      t.id === editingId ? { ...t, text: editText.trim() } : t
    ));
    setEditingId(null);
    setEditText('');
    setHasChanges(true);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const moveTask = (index, direction) => {
    const newTasks = [...tasks];
    const newIndex = index + direction;
    
    if (newIndex < 0 || newIndex >= tasks.length) return;
    
    [newTasks[index], newTasks[newIndex]] = [newTasks[newIndex], newTasks[index]];
    
    // Update IDs to reflect new order
    newTasks.forEach((task, i) => {
      task.id = i + 1;
    });
    
    setTasks(newTasks);
    setHasChanges(true);
  };

  const toggleSkippable = (taskId) => {
    setTasks(tasks.map(t => 
      t.id === taskId ? { ...t, skippable: !t.skippable } : t
    ));
    setHasChanges(true);
  };

  const changePriority = (taskId, priority) => {
    setTasks(tasks.map(t => 
      t.id === taskId ? { ...t, priority } : t
    ));
    setHasChanges(true);
  };

  const addTask = () => {
    const newTask = {
      id: tasks.length + 1,
      text: 'New task',
      priority: 'Home',
      skippable: true,
    };
    setTasks([...tasks, newTask]);
    setHasChanges(true);
    // Start editing the new task
    setEditingId(newTask.id);
    setEditText(newTask.text);
  };

  const deleteTask = (taskId) => {
    if (tasks.length <= 1) {
      toast.error('You must have at least one task');
      return;
    }
    
    const newTasks = tasks.filter(t => t.id !== taskId);
    // Re-index tasks
    newTasks.forEach((task, i) => {
      task.id = i + 1;
    });
    setTasks(newTasks);
    setHasChanges(true);
  };

  const saveAllChanges = () => {
    saveCustomTasks(selectedMode, tasks);
    setHasChanges(false);
    toast.success(`${dayModes[selectedMode].name} tasks saved!`);
  };

  const resetToDefaults = () => {
    if (!window.confirm('Reset to default tasks? This will undo all your customizations for this day mode.')) {
      return;
    }
    resetCustomTasks(selectedMode);
    setTasks([...getDefaultTasks(selectedMode)]);
    setHasChanges(false);
    toast.success('Tasks reset to defaults');
  };

  const modeConfig = dayModes[selectedMode];
  const isCustomized = !!getCustomTasks(selectedMode);

  return (
    <Card className="p-6 bg-paper-light shadow-lifted border border-stone-200">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <Pencil className="w-5 h-5 text-brown" />
          <div>
            <h3 className="font-serif font-medium text-ink-900">Customize Daily Tasks</h3>
            <p className="text-xs text-ink-500 font-accent italic">
              Edit task names, reorder, and add new tasks
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-ink-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-ink-500" />
        )}
      </div>

      {isExpanded && (
        <div className="mt-6 space-y-4 animate-fade-in">
          {/* Mode selector */}
          <div className="space-y-2">
            <Label className="text-ink-700">Select Day Mode to Edit</Label>
            <div className="flex gap-2">
              {Object.values(dayModes).map((mode) => (
                <button
                  key={mode.id}
                  onClick={() => handleModeChange(mode.id)}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    selectedMode === mode.id
                      ? `${mode.bgColor} ${mode.borderColor}`
                      : 'bg-paper border-stone-200 hover:border-stone-300'
                  }`}
                >
                  <span className="text-lg">{mode.emoji}</span>
                  <p className={`text-xs font-medium mt-1 ${
                    selectedMode === mode.id ? mode.textColor : 'text-ink-700'
                  }`}>
                    {mode.name.split(' ')[0]}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Customization status */}
          {isCustomized && (
            <div className="flex items-center gap-2 text-xs text-sage-dark bg-sage/20 px-3 py-2 rounded-lg">
              <Check className="w-4 h-4" />
              <span>Using custom tasks</span>
            </div>
          )}

          {/* Task list */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-ink-700">Tasks</Label>
              <span className="text-xs text-ink-500">{tasks.length} tasks</span>
            </div>
            
            <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
              {tasks.map((task, index) => (
                <div
                  key={task.id}
                  className={`p-3 rounded-lg border transition-all ${
                    task.skippable 
                      ? 'bg-paper border-dashed border-stone-300' 
                      : `${modeConfig.bgColor} ${modeConfig.borderColor}`
                  }`}
                >
                  {editingId === task.id ? (
                    <div className="space-y-2">
                      <Input
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit();
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        className="text-sm"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={saveEdit} className="flex-1">
                          <Check className="w-4 h-4 mr-1" /> Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      {/* Reorder buttons */}
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => moveTask(index, -1)}
                          disabled={index === 0}
                          className="p-1 rounded hover:bg-black/5 disabled:opacity-30"
                        >
                          <ChevronUp className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => moveTask(index, 1)}
                          disabled={index === tasks.length - 1}
                          className="p-1 rounded hover:bg-black/5 disabled:opacity-30"
                        >
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      </div>
                      
                      {/* Task info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink-900 truncate">
                          {task.text}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Select
                            value={task.priority}
                            onValueChange={(value) => changePriority(task.id, value)}
                          >
                            <SelectTrigger className="h-6 text-xs w-24 bg-white/50">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Nutrition">Nutrition</SelectItem>
                              <SelectItem value="Home">Home</SelectItem>
                              <SelectItem value="Habits">Habits</SelectItem>
                            </SelectContent>
                          </Select>
                          <button
                            onClick={() => toggleSkippable(task.id)}
                            className={`text-xs px-2 py-0.5 rounded ${
                              task.skippable 
                                ? 'bg-stone-200 text-ink-500' 
                                : 'bg-sage/30 text-sage-dark'
                            }`}
                          >
                            {task.skippable ? 'Optional' : 'Essential'}
                          </button>
                        </div>
                      </div>
                      
                      {/* Action buttons */}
                      <div className="flex gap-1">
                        <button
                          onClick={() => startEditing(task)}
                          className="p-2 rounded hover:bg-black/5"
                        >
                          <Pencil className="w-4 h-4 text-ink-500" />
                        </button>
                        <button
                          onClick={() => deleteTask(task.id)}
                          className="p-2 rounded hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add task button */}
            <Button
              variant="outline"
              size="sm"
              onClick={addTask}
              className="w-full border-dashed"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Task
            </Button>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 pt-2 border-t border-stone-200">
            <Button
              onClick={saveAllChanges}
              disabled={!hasChanges}
              className="flex-1 vintage-btn btn-brown"
            >
              <Check className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
            <Button
              variant="outline"
              onClick={resetToDefaults}
              className="text-ink-500"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
          </div>

          {hasChanges && (
            <p className="text-xs text-amber-600 text-center animate-pulse">
              You have unsaved changes
            </p>
          )}
        </div>
      )}
    </Card>
  );
};

export default TaskEditor;
